import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Banknote,
  Plus,
  Pencil,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Shield,
  Lock,
  Clock,
  DollarSign,
  Percent,
} from 'lucide-react'
import { loanService, LoanType } from '@/services/loanService'
import LoanTypeModal from '@/components/LoanTypeModal'
import toast from 'react-hot-toast'

const LoanTypesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingType, setEditingType] = useState<LoanType | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['loanTypes'],
    queryFn: () => loanService.getLoanTypes(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => loanService.deleteLoanType(id),
    onSuccess: () => {
      toast.success('Loan type deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['loanTypes'] })
      setDeleteConfirm(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete loan type')
      setDeleteConfirm(null)
    },
  })

  const loanTypes: LoanType[] = data?.data?.loanTypes || data?.data || []

  const filtered = loanTypes.filter(
    (lt) =>
      lt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lt.code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'No limit'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const handleEdit = (lt: LoanType) => {
    setEditingType(lt)
    setShowModal(true)
  }

  const handleCreate = () => {
    setEditingType(null)
    setShowModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setEditingType(null)
  }

  const totalActive = loanTypes.filter((lt) => lt.isActive).length

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Banknote size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Loan Types</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Configure loan categories, amounts, interest rates, and terms
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Total Types</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm">{loanTypes.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Active</div>
            <div className="text-emerald-600 font-bold text-sm">{totalActive}</div>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-all duration-200 hover:shadow-md text-xs"
          >
            <Plus size={12} />
            New Loan Type
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="h-8 w-8 rounded-full border-3 border-indigo-600 border-t-transparent animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
              <Banknote className="text-gray-400 h-5 w-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              {searchTerm ? 'No matching loan types' : 'No Loan Types Yet'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs mb-4">
              {searchTerm ? 'Try a different search term.' : 'Create your first loan type to get started.'}
            </p>
            {!searchTerm && (
              <button
                onClick={handleCreate}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs"
              >
                <Plus size={12} />
                Create Loan Type
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  {['Name', 'Code', 'Interest', 'Min Amount', 'Max Amount', 'Max Term', 'Fee', 'Requirements', 'Status', 'Actions'].map((col) => (
                    <th key={col} className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((lt) => (
                  <tr key={lt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded flex items-center justify-center bg-indigo-50 text-indigo-600">
                          <Banknote size={14} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-xs">{lt.name}</p>
                          {lt.description && (
                            <p className="text-gray-500 dark:text-gray-400 text-xs truncate max-w-[200px]">{lt.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
                        {lt.code}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs font-medium text-gray-900 dark:text-white">
                        <Percent size={10} className="text-amber-500" />
                        {lt.interestRate}%
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs font-medium text-gray-900 dark:text-white">
                        <DollarSign size={10} className="text-emerald-500" />
                        {formatCurrency(lt.minAmount)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs font-medium text-gray-900 dark:text-white">
                        <DollarSign size={10} className="text-emerald-500" />
                        {formatCurrency(lt.maxAmount)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-gray-900 dark:text-white">
                        <Clock size={10} className="text-blue-500" />
                        {lt.maxTermMonths ? lt.maxTermMonths + ' months' : 'No limit'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-gray-900 dark:text-white">
                      {formatCurrency(lt.processingFee)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {lt.requiresGuarantor && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-xs" title="Requires Guarantor">
                            <Shield size={10} /> Guarantor
                          </span>
                        )}
                        {lt.requiresCollateral && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-xs" title="Requires Collateral">
                            <Lock size={10} /> Collateral
                          </span>
                        )}
                        {!lt.requiresGuarantor && !lt.requiresCollateral && (
                          <span className="text-gray-400 text-xs">None</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {lt.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle size={10} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                          <XCircle size={10} /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(lt)}
                          className="text-gray-400 hover:text-indigo-600 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        {deleteConfirm === lt.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => deleteMutation.mutate(lt.id)}
                              className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                              disabled={deleteMutation.isPending}
                            >
                              {deleteMutation.isPending ? '...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(lt.id)}
                            className="text-gray-400 hover:text-red-600 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <LoanTypeModal
          loanType={editingType}
          onClose={handleModalClose}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['loanTypes'] })
            handleModalClose()
          }}
        />
      )}
    </div>
  )
}

export default LoanTypesPage
